import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
  Users,
  Building2,
  FileText,
  Shield,
  CheckCircle,
  Clock,
  TrendingUp,
  Settings,
  BarChart3,
  Eye,
  ArrowRight,
  Layers,
} from 'lucide-react'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { LoadingSpinner } from '../../components/ui/LoadingSpinner'
import { adminApi, AdminStats, AdminExchange } from '../../services/api/admin'
import { useAuth } from '../../contexts/AuthContext'
import { useExchange } from '../../contexts/ExchangeContext'

export const AdminDashboard: React.FC = () => {
  const { user } = useAuth()
  const { exchanges } = useExchange()
  const navigate = useNavigate()
  const [stats, setStats] = useState<AdminStats | null>(null)
  const [recentExchanges, setRecentExchanges] = useState<AdminExchange[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!user?.isAdmin) {
      navigate('/app')
      return
    }

    loadStats()
    loadRecentExchanges()
  }, [user, navigate])

  const loadStats = async () => {
    try {
      setIsLoading(true)
      const data = await adminApi.getStats()
      setStats(data)
    } catch (error) {
      console.error('Failed to load admin stats:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const loadRecentExchanges = async () => {
    try {
      const response = await adminApi.getExchanges({ limit: 5 })
      setRecentExchanges(response.exchanges)
    } catch (error) {
      console.error('Failed to load recent exchanges:', error)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!stats) {
    return null
  }

  const statCards = [
    {
      title: 'Total Users',
      value: stats.totalUsers,
      icon: Users,
      color: 'bg-blue-500',
      link: '/app/admin/users'
    },
    {
      title: 'Total Exchanges',
      value: stats.totalExchanges,
      icon: Building2,
      color: 'bg-green-500',
      link: '/app/admin/exchanges'
    },
    {
      title: 'Active Exchanges',
      value: stats.activeExchanges,
      icon: TrendingUp,
      color: 'bg-emerald-500',
      link: '/app/admin/exchanges?status=active'
    },
    {
      title: 'Verified Users',
      value: stats.verifiedUsers,
      icon: CheckCircle,
      color: 'bg-purple-500',
      link: '/app/admin/users'
    },
    {
      title: 'Pending Verifications',
      value: stats.pendingVerifications,
      icon: Clock,
      color: 'bg-yellow-500',
      link: '/app/admin/users'
    },
    {
      title: 'KYC Complete',
      value: stats.kycComplete,
      icon: Shield,
      color: 'bg-indigo-500',
      link: '/app/admin/users'
    }
  ]

  const quickActions = [
    {
      title: 'Manage Users',
      description: 'View and manage all users',
      icon: Users,
      link: '/app/admin/users',
      color: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
    },
    {
      title: 'Manage Exchanges',
      description: 'View and manage all exchanges',
      icon: Building2,
      link: '/app/admin/exchanges',
      color: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
    },
    {
      title: 'Documents',
      description: 'Manage documents across all exchanges',
      icon: FileText,
      link: '/app/admin/documents',
      color: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800'
    },
    {
      title: 'System Settings',
      description: 'Configure system settings',
      icon: Settings,
      link: '/app/admin/settings',
      color: 'bg-secondary border-border'
    },
    {
      title: 'Style Guide',
      description: 'UI component library and design patterns',
      icon: Layers,
      link: '/styleguide',
      color: 'bg-slate-50 dark:bg-slate-900/20 border-slate-200 dark:border-slate-800'
    },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            <Shield className="h-8 w-8 text-primary" />
            Admin Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage all 1031 exchanges and users
          </p>
        </div>
        <Button onClick={loadStats} variant="outline">
          <BarChart3 className="h-4 w-4 mr-2" />
          Refresh Stats
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {statCards.map((stat) => {
          const Icon = stat.icon
          return (
            <Card
              key={stat.title}
              className="p-6 hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => navigate(stat.link)}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    {stat.title}
                  </p>
                  <p className="text-3xl font-bold text-foreground mt-2">
                    {stat.value}
                  </p>
                </div>
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
              </div>
            </Card>
          )
        })}
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-xl font-semibold text-foreground mb-4">
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions.map((action) => {
            const Icon = action.icon
            return (
              <Card
                key={action.title}
                className={`p-6 hover:shadow-lg transition-all cursor-pointer border-2 ${action.color}`}
                onClick={() => navigate(action.link)}
              >
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-card rounded-lg">
                    <Icon className="h-6 w-6 text-foreground" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-foreground mb-1">
                      {action.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {action.description}
                    </p>
                  </div>
                </div>
              </Card>
            )
          })}
        </div>
      </div>

      {/* Recent Exchanges */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-foreground">
            Recent Exchanges
          </h2>
          <Link to="/app/admin/exchanges">
            <Button variant="outline" size="sm">
              View All
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
        {recentExchanges.length > 0 ? (
          <div className="space-y-3">
            {recentExchanges.map((exchange) => (
              <div
                key={exchange.id}
                className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-secondary/50 transition-colors"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Building2 className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-foreground truncate">
                      {exchange.name}
                    </p>
                    <div className="flex items-center gap-4 mt-1">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        exchange.status === 'active'
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                          : exchange.status === 'pending'
                          ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                          : 'bg-secondary text-secondary-foreground'
                      }`}>
                        {exchange.status}
                      </span>
                      {(exchange.account || exchange.accountName) && (
                        <span className="text-xs text-muted-foreground truncate">
                          {exchange.account?.name || exchange.accountName}
                        </span>
                      )}
                      {exchange.userCount !== undefined && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {exchange.userCount} user{exchange.userCount !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/app/admin/exchanges?view=${exchange.id}`)}
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Building2 className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No exchanges yet</p>
          </div>
        )}
      </Card>

      {/* All Exchanges Overview */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-foreground">
            All Exchanges Overview
          </h2>
          <Link to="/app/admin/exchanges">
            <Button variant="outline" size="sm">
              Manage All
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
            <p className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
              Total Exchanges
            </p>
            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {stats.totalExchanges}
            </p>
            <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
              Across all accounts
            </p>
          </div>
          <div className="p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
            <p className="text-sm font-medium text-green-900 dark:text-green-100 mb-1">
              Active Exchanges
            </p>
            <p className="text-2xl font-bold text-green-600 dark:text-green-400">
              {stats.activeExchanges}
            </p>
            <p className="text-xs text-green-600 dark:text-green-400 mt-1">
              Currently in progress
            </p>
          </div>
          <div className="p-4 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800">
            <p className="text-sm font-medium text-purple-900 dark:text-purple-100 mb-1">
              Total Accounts
            </p>
            <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
              {stats.totalOrganizations}
            </p>
            <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
              Account holders
            </p>
          </div>
        </div>
        {exchanges.length > 0 && (
          <div className="mt-4 p-4 rounded-lg bg-secondary/50">
            <p className="text-sm text-muted-foreground mb-2">
              <strong className="text-foreground">Quick Access:</strong> You can view and manage all {exchanges.length} exchange{exchanges.length !== 1 ? 's' : ''} from the exchange selector in the navigation bar.
            </p>
            <Link to="/app/admin/exchanges">
              <Button variant="outline" size="sm" className="mt-2">
                Go to Exchange Management
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        )}
      </Card>
    </div>
  )
}


